import { useOrgCurrency } from '../../../../../hooks/useOrgCurrency.js';
import { useFormat } from '../../../../../i18n/format.js';
import React from 'react';
import { Euro, AlertTriangle, Calculator, TrendingDown } from 'lucide-react';

/**
 * Financial field configurations per exit reason
 */
const FINANCIAL_CONFIGS = {
  sale: {
    primary: { field: 'sale_price_eur', label: 'Prix de vente', required: true },
    secondary: [],
  },
  lease_end: {
    primary: { field: 'leasing_penalty_amount_eur', label: 'Penalites de restitution', required: false },
    secondary: [
      { field: 'lease_excess_km_cost', label: 'Cout kilometrage excedentaire', required: false },
      { field: 'lease_damage_cost', label: 'Cout des dommages', required: false },
    ],
  },
  claim_theft: {
    primary: { field: 'indemnity_amount_eur', label: 'Montant indemnite assurance', required: false },
    secondary: [],
  },
  claim_accident: {
    primary: { field: 'indemnity_amount_eur', label: 'Montant indemnite assurance', required: false },
    secondary: [
      { field: 'deductible_amount', label: 'Franchise', required: false },
    ],
  },
  claim_other: {
    primary: { field: 'indemnity_amount_eur', label: 'Montant indemnite assurance', required: false },
    secondary: [],
  },
  breakdown: {
    primary: { field: 'scrapyard_price_eur', label: 'Prix de reprise casse', required: false },
    secondary: [],
  },
  seizure: {
    primary: { field: 'accounting_loss_eur', label: 'Perte comptable', required: false },
    secondary: [],
  },
  other: {
    primary: { field: 'other_amount_eur', label: 'Montant financier', required: false },
    secondary: [],
  },
};



/**
 * StepExitFinancials - Financial data step for vehicle exit workflow
 * Dynamic fields based on exit reason
 */
function StepExitFinancials({ formData, updateFormData, vehicle }) {
  // Devise de l'organisation du vehicule - un seul outil monetaire.
  const { money, symbolOf } = useOrgCurrency();
  const { fmt } = useFormat();
  const formatCurrency = (value) => (value ? money(value, vehicle) : '');
  const currencySymbol = symbolOf(vehicle);
  const config = FINANCIAL_CONFIGS[formData.reason] || FINANCIAL_CONFIGS.other;

  // Calculate net result
  const calculateNetResult = () => {
    const purchasePrice = vehicle?.purchase_price || 0;
    let exitAmount = 0;

    if (formData.sale_price_eur) exitAmount = parseFloat(formData.sale_price_eur) || 0;
    else if (formData.indemnity_amount_eur) exitAmount = parseFloat(formData.indemnity_amount_eur) || 0;
    else if (formData.scrapyard_price_eur) exitAmount = parseFloat(formData.scrapyard_price_eur) || 0;
    else if (formData.other_amount_eur) exitAmount = parseFloat(formData.other_amount_eur) || 0;

    const penalties = parseFloat(formData.leasing_penalty_amount_eur) || 0;
    const excessKm = parseFloat(formData.lease_excess_km_cost) || 0;
    const damages = parseFloat(formData.lease_damage_cost) || 0;
    const deductible = parseFloat(formData.deductible_amount) || 0;

    return exitAmount - penalties - excessKm - damages - deductible;
  };

  const netResult = calculateNetResult();

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Donnees Financieres</h3>

      {/* Vehicle Purchase Info */}
      {vehicle?.purchase_price && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Prix d'achat initial</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(vehicle.purchase_price)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Date d'achat</p>
              <p className="text-sm font-medium text-gray-900">
                {vehicle.purchase_date ? fmt.date(vehicle.purchase_date) : '-'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Primary Financial Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {config.primary.label} {config.primary.required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <Euro className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData[config.primary.field] || ''}
            onChange={(e) => updateFormData(config.primary.field, e.target.value)}
            placeholder="0.00"
            className="w-full pl-10 pr-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">{currencySymbol}</span>
        </div>
      </div>

      {/* Secondary Financial Fields */}
      {config.secondary.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {config.secondary.map(field => (
            <div key={field.field}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <Euro className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData[field.field] || ''}
                  onChange={(e) => updateFormData(field.field, e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">{currencySymbol}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lease End Warning */}
      {formData.reason === 'lease_end' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900">Fin de Leasing</h4>
              <p className="text-sm text-amber-700 mt-1">
                Verifiez le decompte final du loueur avant de valider. Les penalites peuvent inclure:
              </p>
              <ul className="text-sm text-amber-700 mt-2 list-disc list-inside">
                <li>Kilometrage excedentaire</li>
                <li>Dommages constates lors de la restitution</li>
                <li>Frais de remise en etat</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Net Result Summary */}
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-900">Resultat net de l'operation</span>
          </div>
          <div className={`text-xl font-bold ${netResult >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {netResult >= 0 ? '+' : ''}{formatCurrency(netResult)}
          </div>
        </div>
        {netResult < 0 && (
          <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
            <TrendingDown className="w-4 h-4" />
            <span>Perte sur cette operation</span>
          </div>
        )}
      </div>

      {/* Notes Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes / Commentaires
        </label>
        <textarea
          value={formData.financial_notes || ''}
          onChange={(e) => updateFormData('financial_notes', e.target.value)}
          placeholder="Informations complementaires sur les aspects financiers..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
        />
      </div>
    </div>
  );
}

export default StepExitFinancials;
export { FINANCIAL_CONFIGS };
